<template>
  <b-card>
    <div class="d-flex justify-content-between align-items-center mb-1">
      <h4 class="mb-0">Вопрос {{ questionIndex }} из 5</h4>
      <b-badge variant="primary">{{ computedPrice }}</b-badge>
    </div>

    <!-- Format selector (if not set on strip) -->
    <b-form-group v-if="!stripFormat" label="Формат вопроса">
      <v-select
        v-model="localFormat"
        :options="formatOptions"
        :reduce="opt => opt.value"
        label="label"
      />
    </b-form-group>

    <!-- Autotopic selector -->
    <b-form-group v-if="artistMode !== 'manual'" label="Автотопик">
      <v-select
        v-model="selectedAutotopic"
        :options="autotopicOptions"
        label="label"
        :placeholder="isLoadingAutotopics ? 'Загрузка…' : '— Без автотопика —'"
        :clearable="true"
        :disabled="isLoadingAutotopics"
      >
        <template #option="{ label, artists_count }">
          <span>{{ label }}</span>
          <small class="text-muted ml-1">({{ artists_count }})</small>
        </template>
        <template #selected-option="{ label, artists_count }">
          <span>{{ label }}</span>
          <small class="text-muted ml-1">({{ artists_count }})</small>
        </template>
      </v-select>
    </b-form-group>

    <!-- Source tabs -->
    <b-tabs v-model="tabIndex" pills nav-class="mb-1">
      <b-tab title="Случайный">
        <div v-if="artistMode === 'random'">
          <b-button size="sm" variant="outline-primary" :disabled="isLoadingArtist" @click="onSuggestArtist">
            <b-spinner v-if="isLoadingArtist" small /> Подобрать
          </b-button>
          <div v-if="selectedArtist" class="mt-1">
            <b-badge variant="info">{{ selectedArtist.title }}</b-badge>
          </div>
        </div>
      </b-tab>

      <b-tab title="Из списка">
        <div v-if="artistMode === 'pick'">
          <v-select
            v-model="selectedArtist"
            :options="artistOptions"
            :filterable="false"
            label="title"
            placeholder="Введите имя артиста"
            @search="onArtistSearch"
            @open="onArtistDropdownOpen"
          />
        </div>
      </b-tab>

      <b-tab title="Вручную">
        <div v-if="artistMode === 'manual'">
          <b-form-group label="Файлы вопроса">
            <b-form-file
              v-model="manualFiles"
              multiple
              :file-name-formatter="formatFileNames"
              placeholder="Выберите один или несколько файлов"
              browse-text="Обзор"
              accept="audio/*,image/*,video/*"
            />
            <small v-if="manualFiles && manualFiles.length" class="text-muted">
              Выбрано файлов: {{ manualFiles.length }}
            </small>
          </b-form-group>
        </div>
      </b-tab>
    </b-tabs>

    <!-- Content depending on format -->
    <div v-if="artistMode !== 'manual' && effectiveFormat && selectedArtist">
      <!-- Tracks: guess melody / cover song -->
      <div v-if="effectiveFormat === 'guess_melody' || effectiveFormat === 'cover_song'">
        <h5>Треки {{ effectiveFormat === 'cover_song' ? '(каверы)' : '' }}</h5>
        <b-spinner v-if="isLoadingTracks" small />
        <b-alert :show="!isLoadingTracks && !tracks.length" variant="warning">Нет подходящих треков</b-alert>

        <b-list-group v-if="tracks.length" class="mb-1 track-list">
          <b-list-group-item
            v-for="t in tracks"
            :key="t.id"
            :active="selectedTrack && selectedTrack.id === t.id"
            class="cursor-pointer track-item"
            @click="selectTrack(t)"
          >
            <div class="d-flex justify-content-between align-items-start">
              <div class="flex-grow-1">
                <div class="track-title">{{ t.track || t.title }}</div>
                <div class="text-muted small">
                  <span v-if="t.artist">{{ t.artist }}</span>
                  <span v-if="t.created_at" class="ml-1">· {{ formatYear(t.created_at) }}</span>
                </div>
              </div>
              <div class="text-right ml-1 d-flex flex-column align-items-end" style="gap: 2px">
                <b-badge v-if="t.play_count" variant="light-secondary">
                  <feather-icon icon="HeadphonesIcon" size="12" /> {{ formatPlayCount(t.play_count) }}
                </b-badge>
                <b-badge v-if="t.difficulty_rating" variant="light-warning">
                  <feather-icon icon="StarIcon" size="12" /> {{ t.difficulty_rating }}
                </b-badge>
                <b-badge v-if="t.is_cover" variant="light-info">кавер</b-badge>
                <b-badge v-if="t.has_covers" variant="light-info">есть каверы</b-badge>
              </div>
            </div>
          </b-list-group-item>
        </b-list-group>

        <strip-audio-cutter
          :audio-url="selectedTrack ? selectedTrack.url : null"
          :value="audioRegion"
          @input="onAudioRegionChange"
        />
      </div>

      <!-- Covers -->
      <div v-else-if="effectiveFormat === 'cover_art'">
        <h5>Обложки</h5>
        <b-spinner v-if="isLoadingCovers" small />
        <b-alert :show="!isLoadingCovers && !covers.length" variant="warning">Нет обложек</b-alert>
        <b-row v-if="covers.length">
          <b-col v-for="c in covers" :key="c.id" cols="3" class="mb-1">
            <b-img-lazy
              :src="c.url"
              fluid
              thumbnail
              class="cursor-pointer"
              :class="{ 'border-primary': selectedCoverIds.includes(c.id) }"
              style="border-width: 3px"
              @click="toggleCover(c)"
            />
          </b-col>
        </b-row>
      </div>
    </div>

    <!-- Answer -->
    <b-form-group label="Ответ" class="mt-1">
      <b-form-input v-model="answer" placeholder="Ответ на вопрос" />
    </b-form-group>

    <!-- Buttons -->
    <div class="d-flex justify-content-end" style="gap: 8px">
      <b-button variant="outline-secondary" @click="$emit('skipped')">Пропустить</b-button>
      <b-button variant="primary" :disabled="!canSave || isSaving" @click="onSave">
        <b-spinner v-if="isSaving" small /> Сохранить и далее
      </b-button>
    </div>
  </b-card>
</template>

<script>
import {
  BCard, BBadge, BFormGroup, BFormInput, BFormFile, BButton, BSpinner, BAlert,
  BListGroup, BListGroupItem, BRow, BCol, BImgLazy, BTabs, BTab,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { ref, computed, watch } from '@vue/composition-api'
import store from '@/store'
import StripAudioCutter from './StripAudioCutter.vue'

const TAB_MODES = ['random', 'pick', 'manual']

export default {
  components: {
    BCard, BBadge, BFormGroup, BFormInput, BFormFile, BButton, BSpinner, BAlert,
    BListGroup, BListGroupItem, BRow, BCol, BImgLazy, BTabs, BTab,
    vSelect, StripAudioCutter,
  },
  props: {
    stripId: { type: [Number, String], required: true },
    questionIndex: { type: Number, required: true },
    stripFormat: { type: String, default: null },
    scaleType: { type: String, default: 'ascending' },
    basePrice: { type: Number, default: null },
    isSaving: { type: Boolean, default: false },
  },
  setup(props, { emit }) {
    const localFormat = ref(null)
    const tabIndex = ref(0)
    const artistMode = computed(() => TAB_MODES[tabIndex.value] || 'random')

    const formatOptions = [
      { label: 'Угадай мелодию', value: 'guess_melody' },
      { label: 'Обложка', value: 'cover_art' },
      { label: 'Кавер', value: 'cover_song' },
    ]

    const selectedArtist = ref(null)
    const isLoadingArtist = ref(false)
    const artistOptions = ref([])

    const autotopicOptions = ref([])
    const selectedAutotopic = ref(null)
    const isLoadingAutotopics = ref(false)

    const tracks = ref([])
    const isLoadingTracks = ref(false)
    const selectedTrack = ref(null)

    const covers = ref([])
    const isLoadingCovers = ref(false)
    const selectedCoverIds = ref([])

    const audioRegion = ref({ startSeconds: 55, durationSeconds: 25 })
    const answer = ref('')

    const manualFiles = ref([])

    const effectiveFormat = computed(() => props.stripFormat || localFormat.value)

    const computedPrice = computed(() => {
      if (props.scaleType === 'flat') return props.basePrice || 100
      return 100 * props.questionIndex
    })

    isLoadingAutotopics.value = true
    store.dispatch('strip-builder/fetchAutotopics')
      .then(response => {
        const list = response.data?.data || response.data || []
        autotopicOptions.value = list.map(a => ({
          label: a.title,
          value: a.id,
          artists_count: a.artists_count,
        }))
      })
      .catch(() => {})
      .finally(() => { isLoadingAutotopics.value = false })

    const onSuggestArtist = () => {
      isLoadingArtist.value = true
      store.dispatch('strip-builder/suggestArtist', {
        stripId: props.stripId,
        autotopicId: selectedAutotopic.value ? selectedAutotopic.value.value : null,
      })
        .then(response => {
          selectedArtist.value = response.data
          isLoadingArtist.value = false
        })
        .catch(() => {
          isLoadingArtist.value = false
        })
    }

    const onArtistSearch = (search, loading) => {
      if (!search || search.length < 2) return
      loading(true)
      store.dispatch('strip-builder/searchArtists', {
        q: search,
        autotopicId: selectedAutotopic.value ? selectedAutotopic.value.value : null,
      })
        .then(response => {
          artistOptions.value = response.data.data || response.data
          loading(false)
        })
        .catch(() => loading(false))
    }

    const onArtistDropdownOpen = () => {
      // pre-populate with first 25 artists from autotopic on open
      if (artistOptions.value.length) return
      store.dispatch('strip-builder/searchArtists', {
        q: '',
        autotopicId: selectedAutotopic.value ? selectedAutotopic.value.value : null,
      })
        .then(response => {
          artistOptions.value = response.data.data || response.data
        })
        .catch(() => {})
    }

    const loadTracks = () => {
      if (!selectedArtist.value) return
      const kind = effectiveFormat.value === 'cover_song' ? 'cover' : 'melody'
      isLoadingTracks.value = true
      store.dispatch('strip-builder/fetchArtistTracks', { artistId: selectedArtist.value.id, kind })
        .then(response => {
          tracks.value = response.data.data || response.data || []
          isLoadingTracks.value = false
        })
        .catch(() => {
          isLoadingTracks.value = false
        })
    }

    const loadCovers = () => {
      if (!selectedArtist.value) return
      isLoadingCovers.value = true
      store.dispatch('strip-builder/fetchArtistCovers', { artistId: selectedArtist.value.id })
        .then(response => {
          covers.value = response.data.data || response.data || []
          isLoadingCovers.value = false
        })
        .catch(() => {
          isLoadingCovers.value = false
        })
    }

    watch([selectedArtist, effectiveFormat], () => {
      tracks.value = []
      covers.value = []
      selectedTrack.value = null
      selectedCoverIds.value = []
      if (!selectedArtist.value || !effectiveFormat.value) return
      if (effectiveFormat.value === 'cover_art') {
        loadCovers()
      } else {
        loadTracks()
      }
    })

    // если меняется автотопик — сбрасываем подобранного артиста и список
    watch(selectedAutotopic, () => {
      selectedArtist.value = null
      artistOptions.value = []
    })

    // при смене таба сбрасываем артиста и manual-файлы
    watch(tabIndex, () => {
      selectedArtist.value = null
      manualFiles.value = []
    })

    const selectTrack = t => {
      selectedTrack.value = t
      answer.value = t.title || ''
    }

    const toggleCover = c => {
      const idx = selectedCoverIds.value.indexOf(c.id)
      if (idx > -1) selectedCoverIds.value.splice(idx, 1)
      else selectedCoverIds.value.push(c.id)
      if (!answer.value && selectedArtist.value) {
        answer.value = selectedArtist.value.title
      }
    }

    const onAudioRegionChange = v => { audioRegion.value = v }

    const formatPlayCount = n => {
      if (!n) return ''
      if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`
      if (n >= 1000) return `${(n / 1000).toFixed(1)}K`
      return String(n)
    }

    const formatYear = iso => {
      if (!iso) return ''
      const d = new Date(iso)
      return Number.isNaN(d.getTime()) ? '' : d.getFullYear()
    }

    const formatFileNames = files => `Файлов: ${files.length}`

    const canSave = computed(() => {
      if (artistMode.value === 'manual') {
        return !!(answer.value && manualFiles.value && manualFiles.value.length)
      }
      if (!effectiveFormat.value) return false
      if (!selectedArtist.value) return false
      if (effectiveFormat.value === 'cover_art') return selectedCoverIds.value.length > 0
      return !!selectedTrack.value
    })

    const onSave = () => {
      if (artistMode.value === 'manual') {
        const formData = new FormData()
        formData.append('answer', answer.value)
        formData.append('order', props.questionIndex)
        manualFiles.value.forEach(f => formData.append('files[]', f))
        emit('accepted-manual', { formData })
        return
      }

      const payload = {
        format: effectiveFormat.value,
        order: props.questionIndex,
        answer: answer.value,
      }
      if (selectedArtist.value) payload.artist_id = selectedArtist.value.id
      if (effectiveFormat.value === 'cover_art') {
        payload.cover_ids = selectedCoverIds.value
      } else {
        payload.track_id = selectedTrack.value.id
        payload.start_seconds = audioRegion.value.startSeconds
        payload.duration_seconds = audioRegion.value.durationSeconds
      }
      emit('accepted', payload)
    }

    return {
      localFormat, effectiveFormat, formatOptions, tabIndex, artistMode,
      selectedArtist, isLoadingArtist, artistOptions,
      autotopicOptions, selectedAutotopic, isLoadingAutotopics,
      tracks, isLoadingTracks, selectedTrack, covers, isLoadingCovers, selectedCoverIds,
      audioRegion, answer, computedPrice, canSave,
      manualFiles,
      onSuggestArtist, onArtistSearch, onArtistDropdownOpen,
      selectTrack, toggleCover, onAudioRegionChange,
      onSave, formatPlayCount, formatYear, formatFileNames,
    }
  },
}
</script>

<style scoped>
.track-list {
  max-height: 480px;
  overflow-y: auto;
}
.track-item {
  padding: 0.5rem 0.75rem;
}
.track-title {
  font-weight: 500;
}
</style>
